/**
 * Typed bindings for /api/api-credentials and /api/admin/api-credential-presets.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";

export type ClientType =
  | "desktop_windows"
  | "desktop_mac"
  | "desktop_linux"
  | "android"
  | "ios";

export interface ApiCredentialSummary {
  id: string;
  label: string;
  api_id: number;
  api_id_masked: string;
  is_burned: boolean;
  burn_reason: string | null;
  accounts_count: number;
  banned_count: number;
  notes: string | null;
  created_at: string;
}

export interface ApiCredentialDetail extends ApiCredentialSummary {
  api_hash: string;
}

export const CREDENTIALS_KEY = ["api-credentials"] as const;

export function useCredentials() {
  return useQuery<ApiCredentialSummary[]>({
    queryKey: CREDENTIALS_KEY,
    queryFn: () => api.get<ApiCredentialSummary[]>("/api/api-credentials"),
  });
}

export function useCreateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      label: string;
      api_id: number;
      api_hash: string;
      notes?: string | null;
    }) => api.post<ApiCredentialSummary>("/api/api-credentials", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CREDENTIALS_KEY }),
  });
}

export function useUpdateCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string; label?: string; notes?: string | null }) =>
      api.patch<ApiCredentialSummary>(`/api/api-credentials/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: CREDENTIALS_KEY }),
  });
}

export function useDeleteCredential() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/api-credentials/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: CREDENTIALS_KEY }),
  });
}

// ---------------------------------------------------------------------------
// Public presets — read-only list for any authed company user
// (used in the account import dropdown). No api_hash field.
// ---------------------------------------------------------------------------

export interface PublicPreset {
  id: string;
  label: string;
  client_type: ClientType;
  api_id: number;
  lang_code: string | null;
  has_device_template: boolean;
}

export const PUBLIC_PRESETS_KEY = ["api-credentials", "presets"] as const;

export function usePublicPresets() {
  return useQuery<PublicPreset[]>({
    queryKey: PUBLIC_PRESETS_KEY,
    queryFn: () => api.get<PublicPreset[]>("/api/api-credentials/presets"),
    // Presets change rarely; cache for the session.
    staleTime: 5 * 60 * 1000,
  });
}

export interface PresetTemplate extends PublicPreset {
  api_hash: string;
}

export async function fetchPresetTemplate(id: string): Promise<PresetTemplate> {
  return api.get<PresetTemplate>(`/api/api-credentials/presets/${id}/template`);
}

// ---------------------------------------------------------------------------
// Platform presets (superadmin only)
// ---------------------------------------------------------------------------

export interface PresetSummary {
  id: string;
  label: string;
  client_type: ClientType;
  api_id: number;
  is_active: boolean;
  has_device_template: boolean;
  lang_code: string | null;
  created_at: string;
}

export const PRESETS_KEY = ["admin", "presets"] as const;

export function usePresets() {
  return useQuery<PresetSummary[]>({
    queryKey: PRESETS_KEY,
    queryFn: () => api.get<PresetSummary[]>("/api/admin/api-credential-presets"),
  });
}

export function useCreatePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      label: string;
      client_type: ClientType;
      api_id: number;
      api_hash: string;
      device_model_template?: string[] | null;
      system_version_template?: string[] | null;
      app_version_template?: string[] | null;
      lang_code?: string | null;
    }) => api.post<PresetSummary>("/api/admin/api-credential-presets", payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRESETS_KEY }),
  });
}

export function useDeletePreset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.delete<void>(`/api/admin/api-credential-presets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: PRESETS_KEY }),
  });
}
